import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ROUTES_METADATA, DEFAULT_META, BASE_DOMAIN } from 'src/config/routesMetadata.config.js'

let instance = null

export function useMetadata() {
  if (instance) {
    return instance
  }

  const route = useRoute()

  const currentMeta = ref({
    title: DEFAULT_META.title,
    description: DEFAULT_META.description,
    url: DEFAULT_META.url,
  })

  const dynamicData = ref(null)

  const metaTags = computed(() => ({
    title: currentMeta.value.title,
    meta: [
      { name: 'description', content: currentMeta.value.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: currentMeta.value.url },
      { property: 'og:title', content: currentMeta.value.title },
      { property: 'og:description', content: currentMeta.value.description },
      { property: 'og:image', content: DEFAULT_META.image },
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:url', content: currentMeta.value.url },
      { property: 'twitter:title', content: currentMeta.value.title },
      {
        property: 'twitter:description',
        content: currentMeta.value.description,
      },
      { property: 'twitter:image', content: DEFAULT_META.image },
    ],
  }))

  function setDynamicData(data) {
    dynamicData.value = data
  }

  function generateMetadata() {
    const url = `${BASE_DOMAIN}${route.fullPath}`
    const config = ROUTES_METADATA[route.name]

    if (!config) {
      currentMeta.value = {
        title: DEFAULT_META.title,
        description: DEFAULT_META.description,
        url,
      }
      return
    }

    if (config.getDynamicMeta && dynamicData.value) {
      const dynamic = config.getDynamicMeta(route, dynamicData.value)
      if (dynamic) {
        currentMeta.value = {
          title: dynamic.title,
          description: dynamic.description || config.meta.description,
          url,
        }
        return
      }
    }

    currentMeta.value = {
      title: config.meta.title,
      description: config.meta.description,
      url,
    }
  }

  watch(
    () => route.fullPath,
    () => {
      const nextConfig = ROUTES_METADATA[route.name]
      if (!nextConfig?.getDynamicMeta) {
        dynamicData.value = null
      }
      generateMetadata()
    },
    { immediate: true }
  )

  watch(dynamicData, generateMetadata)

  instance = { metaTags, generateMetadata, setDynamicData }

  return instance
}
